import type { EntityType, InitPayloadType, StaticEntityType } from "@config";
import { nanoid } from "@reduxjs/toolkit";
import flatten from "lodash/flatten";

// =========== HELPERS ===========

type SlideItemType = {
  id: string;
  type: string;
  settings: Record<string, any>;
  children: SlideItemType[];
};

export const normalizeData = (data: Record<string, any>) => {
  const entities = {} as Record<string, EntityType>;
  const slideIds: string[] = [];
  const createEntity = (
    settings: any,
    type: "slide" | "combo" | "set" | "group" | "element",
    parentId: string | null,
  ) => {
    const id = nanoid(20);
    const entity: EntityType = {
      settings,
      id,
      childIds: [],
      type,
      parentId,
    };
    entities[id] = entity;
    return entity;
  };

  data.slides.forEach((slide: Record<string, any>) => {
    const slideEntity = createEntity(slide.settings, "slide", "root");
    slideIds.push(slideEntity.id);

    slide.combos.forEach((combo: Record<string, any>) => {
      const name = combo.name;
      const comboEntity = createEntity(
        { ...combo.settings, name: combo.name },
        "combo",
        slideEntity.id,
      );
      slideEntity.childIds.push(comboEntity.id);

      combo.sets.forEach((set: Record<string, any>) => {
        const { uniqueKey, isAddElement, groups, ...settings } = set;
        const setEntity = createEntity(settings, "set", comboEntity.id);
        comboEntity.childIds.push(setEntity.id);
        if (name === "Array" && set.groups.length > 1) {
          set.groups = [[...set.groups[0], ...set.groups[1]]];
        }

        set.groups.forEach((group: Record<string, any>) => {
          if (group.length === 0) return;
          const groupEntity = createEntity({}, "group", setEntity.id);
          setEntity.childIds.push(groupEntity.id);

          group.forEach((element: Record<string, any>) => {
            const data = element.data;
            if (element.name === "Image") {
              if (data.updatedSrc) data.src = data.updatedSrc;
              delete data.updatedSrc;
            }

            const elementEntity = createEntity(
              { ...data, name: element.name },
              "element",
              groupEntity.id,
            );
            groupEntity.childIds.push(elementEntity.id);
          });
        });
      });

      if (name === "Tabs") {
        const { tabsItems, ...tabsSettings } = comboEntity.settings;
        const { design, titles } = tabsItems as Record<string, any>;
        entities[comboEntity.id].settings = {
          ...tabsSettings,
          tabsDesign: design,
        };
        const childIds = [] as string[];
        titles.forEach((tab: Record<string, any>, index: number) => {
          const { name, icon, setIndex, isDefault } = tab;
          const set = entities[comboEntity.childIds[setIndex]];
          set.settings = {
            ...set.settings,
            tabTitle: name,
            tabIcon: icon,
            isTabDefault: isDefault,
          };
          childIds[index] = set.id;
        });

        comboEntity.childIds = childIds;
      }
    });
  });

  entities["root"] = {
    id: "root",
    settings: {},
    childIds: slideIds,
    type: "root",
    parentId: null,
  };
  const { slides, ...rest } = data;

  return {
    ...rest,
    flatContentLayers: entities,
  } as InitPayloadType;
};

/**
 * Self-heal a corrupted entity map. Two integrity violations are repaired:
 *
 *  1. Dangling childIds — a parent's `childIds` lists an id whose entity is no
 *     longer in the map (e.g. a collab reconcile / undo-redo index desync left
 *     a `set` referenced after its entity was deleted). Left in place this
 *     hard-crashes `invertFlatData` (autosave, publish, content-hash, preview),
 *     so the whole editor goes down on the next mutation.
 *  2. Orphaned entities — entities unreachable from `root` (e.g. the descendants
 *     of a deleted set that survived in the map). These bloat the doc and are
 *     never rendered.
 *
 * The walk starts at `root` and keeps only what is reachable through *existing*
 * children, rebuilding any `childIds` array that had to drop a dangling id. A
 * valid tree is returned untouched (referentially equal) so callers can cheaply
 * skip re-assignment when nothing changed.
 */
export const repairFlatContentLayers = (
  flatData: Record<string, EntityType>,
): { repaired: Record<string, EntityType>; changed: boolean } => {
  const root = flatData["root"];
  // Without a root we cannot determine reachability — leave the data as-is.
  if (!root) return { repaired: flatData, changed: false };

  const reachable: Record<string, EntityType> = {};
  let changed = false;

  const walk = (id: string) => {
    const entity = flatData[id];
    if (!entity || reachable[id]) return;
    const validChildIds = entity.childIds.filter((childId) => !!flatData[childId]);
    if (validChildIds.length !== entity.childIds.length) changed = true;
    reachable[id] = validChildIds.length === entity.childIds.length
      ? entity
      : { ...entity, childIds: validChildIds };
    validChildIds.forEach(walk);
  };

  walk("root");

  // Any entity not visited by the walk is an unreachable orphan.
  if (Object.keys(reachable).length !== Object.keys(flatData).length) changed = true;

  return { repaired: changed ? reachable : flatData, changed };
};

// An element entity may own element children (e.g. a Table's in-cell elements,
// linked back to their cell via `settings.cellId`); nest them recursively. The
// `children` key is omitted for leaves so pre-existing content serializes
// byte-identically (content hashes and autosave diffs must not shift).
const invertElement = (
  elementId: string,
  flatData: Record<string, EntityType>,
  resetIds?: boolean,
): Record<string, any> | null => {
  const elementEntity = flatData[elementId];
  if (!elementEntity) return null;
  const element: Record<string, any> = {
    id: resetIds ? nanoid(15) : elementId,
    type: "element",
    settings: elementEntity.settings,
  };
  const children = elementEntity.childIds
    .map((childId) => invertElement(childId, flatData, resetIds))
    .filter((child): child is Record<string, any> => child !== null);
  if (children.length > 0) element.children = children;
  return element;
};

export const invertFlatData = ({
  flatData,
  rootId,
  resetIds,
}: {
  flatData: Record<string, EntityType>;
  rootId?: string;
  resetIds?: boolean;
}): Record<string, any>[] => {
  const root = flatData[rootId || "root"];
  const nestedData: Record<string, any>[] = [];
  if (!root) return nestedData;

  root.childIds.forEach((slideId: string) => {
    const slideEntity = flatData[slideId];
    // Skip dangling references: a childId whose entity was removed from the map
    // (e.g. via a collab reconcile / undo-redo desync) must not be dereferenced.
    if (!slideEntity) return;
    const slide: Record<string, any> = {
      id: resetIds ? nanoid(15) : slideId,
      type: "slide",
      settings: slideEntity.settings,
      children: [],
    };

    slideEntity.childIds.forEach((comboId: string) => {
      const comboEntity = flatData[comboId];
      if (!comboEntity) return;
      const combo: Record<string, any> = {
        id: resetIds ? nanoid(15) : comboId,
        type: "combo",
        settings: comboEntity.settings,
        children: [],
      };

      comboEntity.childIds.forEach((setId: string) => {
        const setEntity = flatData[setId];
        if (!setEntity) return;
        const set: Record<string, any> = {
          id: resetIds ? nanoid(15) : setId,
          type: "set",
          settings: setEntity.settings,
          children: [],
        };

        setEntity.childIds.forEach((groupId: string) => {
          const groupEntity = flatData[groupId];
          if (!groupEntity) return;
          const group: Record<string, any> = {
            id: resetIds ? nanoid(15) : groupId,
            type: "group",
            settings: {},
            children: [],
          };

          groupEntity.childIds.forEach((elementId: string) => {
            const element = invertElement(elementId, flatData, resetIds);
            if (element) group.children.push(element);
          });

          set.children.push(group);
        });

        combo.children.push(set);
      });

      slide.children.push(combo);
    });

    nestedData.push(slide);
  });

  return nestedData;
};

export const invertFlatComboData = (
  flatData: Record<string, EntityType>,
  rootId?: string,
): Record<string, any>[] => {
  const nestedData: Record<string, any>[] = [];

  const comboEntity = flatData[rootId || "root"];
  if (!comboEntity) return nestedData;
  const combo: Record<string, any> = {
    id: rootId || "root",
    type: "combo",
    settings: comboEntity.settings,
    children: [],
  };

  comboEntity.childIds.forEach((setId: string) => {
    const setEntity = flatData[setId];
    if (!setEntity) return;
    const set: Record<string, any> = {
      id: setId,
      type: "set",
      settings: setEntity.settings,
      children: [],
    };

    setEntity.childIds.forEach((groupId: string) => {
      const groupEntity = flatData[groupId];
      if (!groupEntity) return;
      const group: Record<string, any> = {
        id: groupId,
        type: "group",
        settings: {},
        children: [],
      };

      groupEntity.childIds.forEach((elementId: string) => {
        const element = invertElement(elementId, flatData);
        if (element) group.children.push(element);
      });

      set.children.push(group);
    });

    combo.children.push(set);
  });
  nestedData.push(combo);

  return nestedData;
};

export const normalizeToNestedData = (
  slides: Record<string, any>[],
): Record<string, any>[] => {
  const nestedData: Record<string, any>[] = [];

  slides.forEach((slide: Record<string, any>) => {
    const slideEntity: SlideItemType = {
      id: nanoid(15),
      type: "slide",
      settings: slide.settings,
      children: [],
    };

    slide.combos.forEach((combo: Record<string, any>) => {
      const comboEntity: SlideItemType = {
        id: nanoid(15),
        type: "combo",
        settings: { ...combo.settings, name: combo.name },
        children: [],
      };

      combo.sets.forEach((set: Record<string, any>) => {
        const { uniqueKey, isAddElement, groups, ...settings } = set;
        const setEntity: SlideItemType = {
          id: nanoid(15),
          type: "set",
          settings,
          children: [],
        };

        if (combo.name === "Array" && set.groups.length > 1) {
          set.groups = [(set.groups = flatten(set.groups))];
        }

        set.groups.forEach((group: Record<string, any>) => {
          if (group.length === 0) return;
          const groupEntity: SlideItemType = {
            id: nanoid(15),
            type: "group",
            settings: {},
            children: [],
          };

          group.forEach((element: Record<string, any>) => {
            const elementEntity: SlideItemType = {
              id: nanoid(15),
              type: "element",
              settings: { ...element.data, name: element.name },
              children: [],
            };
            groupEntity.children.push(elementEntity);
          });

          setEntity.children.push(groupEntity);
        });
        comboEntity.children.push(setEntity);
      });

      if (comboEntity.settings.name === "Tabs") {
        const { tabsItems, ...tabsSettings } = comboEntity.settings;
        const { design, titles } = tabsItems as Record<string, any>;
        comboEntity.settings = {
          ...tabsSettings,
          tabsDesign: design,
        };

        titles.forEach((tab: Record<string, any>) => {
          const { name, icon, setIndex, isDefault } = tab;
          const set = comboEntity.children[setIndex];
          set.settings = {
            ...set.settings,
            tabTitle: name,
            tabIcon: icon,
            isTabDefault: isDefault,
            id: set.id,
          };
          comboEntity.children = comboEntity.children.filter(
            (child: StaticEntityType) => child.id !== set.id,
          );
          comboEntity.children.splice(setIndex, 0, set);
        });
      }

      slideEntity.children.push(comboEntity);
    });

    nestedData.push(slideEntity);
  });

  return nestedData;
};

const createEntity = (
  settings: any,
  type: "slide" | "combo" | "set" | "group" | "element",
  parentId: string | null,
  entities: Record<string, EntityType>,
  entryId?: string,
  entityId?: string,
) => {
  const id = entityId || entryId || nanoid(20);
  const entity: EntityType = {
    settings,
    id,
    childIds: [],
    type,
    parentId,
  };
  entities[id] = entity;
  return entity;
};

export const normalizedFromNestedStructured = (
  slides: Record<string, any>[],
  rootId: string | null,
  startType: "slide" | "combo" = "slide",
  entryId?: string,
  story?: boolean,
  keepIds?: boolean,
  isVersionParse?: boolean,
) => {
  const entities = {} as Record<string, EntityType>;
  const slideIds: string[] = [];
  let root = "";

  // Elements may carry nested element children (table in-cell elements);
  // `children` is absent on leaves — see invertElement.
  const processElement = (
    element: Record<string, any>,
    parentId: string,
  ): EntityType => {
    const elementEntity = createEntity(
      element.settings,
      "element",
      parentId,
      entities,
      "",
      keepIds ? element.id : undefined,
    );
    (element.children ?? []).forEach((child: Record<string, any>) => {
      const childEntity = processElement(child, elementEntity.id);
      elementEntity.childIds.push(childEntity.id);
    });
    return elementEntity;
  };

  const processCombo = (
    combo: Record<string, any>,
    parentId: string | null,
  ) => {
    const comboEntity = createEntity(
      combo.settings,
      "combo",
      parentId,
      entities,
      entryId,
      keepIds ? combo.id : undefined,
    );
    if (startType === "slide")
      entities[parentId || ""].childIds.push(comboEntity.id);
    if (startType === "combo") root = comboEntity.id;

    combo.children.forEach((set: Record<string, any>) => {
      const setEntity = createEntity(
        set.settings,
        "set",
        comboEntity.id,
        entities,
        "",
        keepIds ? set.id : undefined,
      );
      comboEntity.childIds.push(setEntity.id);

      set.children.forEach((group: Record<string, any>) => {
        if (group.length === 0) return;
        const groupEntity = createEntity(
          {},
          "group",
          setEntity.id,
          entities,
          "",
          keepIds ? group.id : undefined,
        );
        setEntity.childIds.push(groupEntity.id);

        group.children.forEach((element: Record<string, any>) => {
          const elementEntity = processElement(element, groupEntity.id);
          groupEntity.childIds.push(elementEntity.id);
        });
      });
    });
  };

  slides.forEach((slide: Record<string, any>) => {
    if (startType === "slide") {
      const slideEntity = createEntity(
        slide.settings,
        "slide",
        rootId,
        entities,
        "",
        keepIds ? slide.id : undefined,
      );
      root = slideEntity.id;
      if (story) slideIds.push(slideEntity.id);
      slide.children.forEach((combo: Record<string, any>) =>
        processCombo(combo, slideEntity.id),
      );
    } else if (startType === "combo") {
      slide.children.forEach((combo: Record<string, any>) => {
        processCombo(combo, rootId);
      });
    }
  });

  if (story) {
    entities["root"] = {
      id: "root",
      settings: {},
      childIds: slideIds,
      type: "root",
      parentId: null,
    };
  }

  return { entities, root };
};
